import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Eye, Send } from "lucide-react";
import { CreateProposalDialog } from "@/components/proposals/CreateProposalDialog";
import { toast } from "sonner";

interface Proposal {
  id: string;
  title: string;
  status: string;
  created_at: string;
  public_share_id: string | null;
}

interface ProposalsTabProps {
  jobId: string;
  proposals: Proposal[];
  jobData?: any;
  onCreateProposal: () => void;
}

export function ProposalsTab({ jobId, proposals, jobData, onCreateProposal }: ProposalsTabProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [estimateId, setEstimateId] = useState<string | null>(null);

  // Auto-open dialog if URL params present
  useEffect(() => {
    const shouldCreate = searchParams.get('createProposal') === 'true';
    const estimateParam = searchParams.get('estimateId');
    
    if (shouldCreate) {
      setEstimateId(estimateParam);
      setShowCreateDialog(true);
      // Clear params after opening
      searchParams.delete('createProposal');
      searchParams.delete('estimateId');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEstimateId(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "signed":
        return "bg-success text-success-foreground";
      case "sent":
        return "bg-info text-info-foreground";
      case "viewed":
        return "bg-warning text-warning-foreground";
      default:
        return "bg-secondary text-secondary-foreground";
    }
  };

  return (
    <div className="container mx-auto px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Proposals</h2>
        <Button onClick={onCreateProposal} className="gap-2">
          <Plus className="w-4 h-4" />
          Create New Proposal
        </Button>
      </div>

      {proposals.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No proposals created yet</p>
            <Button variant="outline" onClick={onCreateProposal}>
              Create Your First Proposal
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {proposals.map((proposal) => (
            <Card key={proposal.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{proposal.title}</h3>
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(proposal.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <Badge className={getStatusColor(proposal.status)}>
                    {proposal.status}
                  </Badge>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => navigate(`/proposals/review/${proposal.id}`)}
                  >
                    <Eye className="w-4 h-4" />
                    View Proposal
                  </Button>
                  {proposal.public_share_id && (
                    <Button variant="outline" className="w-full gap-2" size="sm">
                      <Send className="w-4 h-4" />
                      Share Link
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateProposalDialog
        open={showCreateDialog}
        onOpenChange={handleCloseDialog}
        jobId={jobId}
        jobData={jobData || {}}
        estimateId={estimateId}
      />
    </div>
  );
}
